import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './ResetPassword.module.css';
import AuthService from '../../../services/auth/AuthService';

// Mirror the backend's StrongPasswordValidator: 8+ chars, upper, lower, digit, and one of @$!%*?&.
function validatePasswordStrength(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter.';
  if (!/\d/.test(password)) return 'Password must contain a digit.';
  if (!/[@$!%*?&]/.test(password)) return 'Password must contain a special character (@ $ ! % * ? &).';
  return null;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Capture the token once on mount and immediately strip it from the URL so it
  // can't leak via window.location, the back button, the Referer header on
  // outbound links, browser history, or third-party scripts on the page.
  const initialToken = useMemo(() => searchParams.get('token') ?? '', []); // eslint-disable-line react-hooks/exhaustive-deps
  const [token] = useState(initialToken);

  useEffect(() => {
    if (initialToken) {
      window.history.replaceState({}, '', '/reset-password');
    }
  }, [initialToken]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is invalid or has expired. Please request a new one.');
      return;
    }
    if (!password || !confirm) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const strengthError = validatePasswordStrength(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }

    try {
      setLoading(true);
      await AuthService.resetPassword(token, password);
      setSubmitted(true);
      // Don't auto-login: make the user sign in with the new password so any
      // pre-existing sessions/tokens are re-validated server-side.
    } catch {
      setError('This reset link is invalid or has expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.successState}>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.heading}>Password updated</h1>
            <p className={styles.subheading}>
              Your password has been reset. You can now sign in with your new password.
            </p>
            <button
              type="button"
              className={styles.button}
              onClick={() => navigate('/login', { replace: true })}
            >
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.heading}>Choose a new password</h1>
        <p className={styles.subheading}>
          Enter a new password for your account. Use at least 8 characters with upper and lower
          case letters, a digit, and a special character.
        </p>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">New password</label>
            <input
              id="password"
              type="password"
              className={styles.input}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirm">Confirm new password</label>
            <input
              id="confirm"
              type="password"
              className={styles.input}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? <span className={styles.spinner} /> : 'Update password'}
          </button>
        </form>

        <p className={styles.footer}>
          Remembered it?{' '}
          <a href="/login" className={styles.link}>Back to sign in</a>
        </p>
      </div>
    </div>
  );
}
